import { useState } from "react";
import { Box, Button, Text, useToast, HStack, Tooltip } from "@chakra-ui/react";
import { executeCode } from "../api";
import { useWindowSize } from "../../useWindowSize";
import logger from './../../utils/logger.js';


const Output = ({ editorRef, language, onRun }) => {
  const toast = useToast();
  const [output, setOutput] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width < 700;
  const isSQL = language.toLowerCase() === "sql";

  const runCode = async () => {
    if (!editorRef.current) {
      toast({
        title: "Error",
        description: "Editor not initialized",
        status: "error",
        duration: 2000,
      });
      return;
    }

    const code = editorRef.current.getValue();
    
    try {
      // Call the onRun function to auto-save the code
      if (onRun && typeof onRun === 'function') {
        // Auto-save in a non-blocking way
        Promise.resolve().then(() => {
          if (import.meta.env.MODE !== 'production') {
            logger.debug("Auto-saving code on Run Code button press");
          }
          onRun();
        });
      }
      
      setIsLoading(true);
      const { run: result } = await executeCode(language, code);
      setOutput(result.output.split("\n"));
      result.stderr ? setIsError(true) : setIsError(false);
    } catch (error) {
      logger.debug(error);
      toast({
        title: "An error occurred.",
        description: error.message || "Unable to run code",
        status: "error",
        duration: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box w={isMobile ? "100%" :"70%"} mb={10} pt={2}>
      <HStack spacing={2} mb={2}>
        {!isSQL && (
          <Button
            variant="outline"
            colorScheme="green"
            isLoading={isLoading}
            onClick={runCode}
          >
            Run Code
          </Button>
        )}
      </HStack>
      
      {!isSQL && (
        <>
          <Text mb={2} fontSize="lg">
            Output
          </Text>
          
          <Box
            height="64vh"
            p={2}
            color={isError ? "red.400" : ""}
            border="1px solid"
            borderRadius={4}
            borderColor={isError ? "red.500" : "#333"}
            overflowY="auto"
          >
            {output
              ? output.map((line, i) => <Text key={i}>{line}</Text>)
              : 'Click "Run Code" to see the output here'}
          </Box>
        </>
      )}
      
      {/* Add keyframe animation for pulsing effect */}
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default Output;
